import { FC, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { GroupBase, OptionsOrGroups } from "react-select";
import { useDebouncedCallback } from "use-debounce";

import HubInputField from "../../inputType";
import useFetch from "src/hooks/useFetch";

import { US_STATES } from "src/constants";
import { PATIENT_ADDR_TYPES } from "src/constants/cases";
import { AddPatientAddressProps } from "./types";
import { APIEndpoints } from "src/types/apiTypes";

type PlacePrediction = {
  description: string;
  place_id: string;
  structured_formatting: { main_text: string; secondary_text: string };
};

type AutocompleteResponse = {
  predictions: PlacePrediction[];
  status: string;
};

const dropdownStyle: React.CSSProperties = {
  position: "absolute",
  top: "100%",
  left: 0,
  right: 0,
  zIndex: 1000,
  backgroundColor: "#fff",
  border: "1px solid #ccc",
  borderRadius: "4px",
  margin: 0,
  padding: 0,
  listStyle: "none",
  boxShadow: "0 4px 8px rgba(0,0,0,0.12)",
  maxHeight: "220px",
  overflowY: "auto",
};

const AddCasePatientAddress: FC<AddPatientAddressProps> = ({ control, setValue }): JSX.Element => {

  const [inputLength, setInputLength] = useState(0);
  const [activeIndex, setActiveIndex] = useState(-1);

  const { mutate: fetchPlaceAutocomplete, data: placeResults } = useFetch<AutocompleteResponse>(
    APIEndpoints.AUTOCOMPLETE_PLACE, undefined, undefined, false
  );

  const { mutate: fetchPlaceDetails, data: placeDetails } = useFetch(
    APIEndpoints.PLACE_DETAILS, undefined, undefined, false
  );

  useEffect(() => {
    if (!placeDetails) return;
    const { zip, city, state } = placeDetails as any;
    if (zip) setValue("patient_attributes.address.zip", zip, { shouldValidate: true });
    if (city) setValue("patient_attributes.address.city", city, { shouldValidate: true });
    if (state) setValue("patient_attributes.address.state", state, { shouldValidate: true });
  }, [placeDetails, setValue]);

  useEffect(() => { setActiveIndex(-1); }, [placeResults]);

  const searchPlace = useDebouncedCallback((value: string) => {
    if (value.length >= 3) fetchPlaceAutocomplete(undefined, undefined, { place: value });
  }, 400);

  const handleSelect = (prediction: PlacePrediction, fieldOnChange: (val: string) => void) => {
    fieldOnChange(prediction.structured_formatting.main_text);
    setInputLength(0);
    fetchPlaceDetails(undefined, undefined, { place_id: prediction.place_id });
    setValue("patient_attributes.address.street", prediction.structured_formatting.main_text, { shouldValidate: true });
  };

  return (
    <div className="card m-1 mb-3">
      <div className="card-header">
        <h4 className="m-0">Patient Address</h4>
      </div>
      <div className="card-body">
        <div className="row">
          <label className="form-label col-lg-2 col-md-2 col-12">Address *</label>
          <div className="col-lg-8 col-md-6">
            <Controller
              name="patient_attributes.address.street"
              control={control}
              defaultValue=""
              render={({ field, fieldState: { error } }) => (
                <>
                  <div style={{ position: "relative" }}>
                    <input
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        setInputLength(e.target.value.length);
                        searchPlace(e.target.value);
                      }}
                      onBlur={() => setInputLength(0)}
                      onKeyDown={(e) => {
                        const predictions = placeResults?.predictions ?? [];
                        if (e.key === "ArrowDown") {
                          e.preventDefault();
                          setActiveIndex((i) => Math.min(i + 1, predictions.length - 1));
                        } else if (e.key === "ArrowUp") {
                          e.preventDefault();
                          setActiveIndex((i) => Math.max(i - 1, 0));
                        } else if (e.key === "Enter" && activeIndex >= 0) {
                          e.preventDefault();
                          handleSelect(predictions[activeIndex], field.onChange);
                        } else if (e.key === "Escape") {
                          setInputLength(0);
                        }
                      }}
                      className="form form-control w-100"
                      type="text"
                      placeholder="Street*"
                      autoComplete="off"
                    />
                    {inputLength > 0 && inputLength < 3 && (
                      <ul style={dropdownStyle}>
                        <li style={{ padding: "10px 14px", color: "#999", fontSize: "13px" }}>
                          Please enter at least 3 characters to search
                        </li>
                      </ul>
                    )}
                    {inputLength >= 3 && placeResults?.predictions?.length ? (
                      <ul style={dropdownStyle}>
                        {placeResults.predictions.map((prediction, index) => (
                          <li
                            key={prediction.place_id}
                            onMouseDown={() => handleSelect(prediction, field.onChange)}
                            onMouseEnter={() => setActiveIndex(index)}
                            onMouseLeave={() => setActiveIndex(-1)}
                            style={{
                              padding: "8px 4px",
                              cursor: "pointer",
                              borderBottom: "1px solid #f0f0f0",
                              fontSize: "13px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              backgroundColor: activeIndex === index ? "#f5f5f5" : "#fff",
                            }}
                          >
                            <i className="bi bi-geo-alt-fill me-2 text-muted"></i>
                            <strong>{prediction.structured_formatting.main_text}</strong>
                            {" "}
                            <span style={{ color: "#777" }}>{prediction.structured_formatting.secondary_text}</span>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                  {error?.message && (
                    <div
                      dangerouslySetInnerHTML={{ __html: error.message }}
                      className="text-danger text-small mt-2"
                    />
                  )}
                </>
              )}
            />
          </div>
          <div className="col-lg-2 col-md-4">
            <Controller name="patient_attributes.address.suite" control={control} defaultValue=""
              render={({ field, fieldState: { error } }) => (
                <HubInputField isRowBased={true} isRequired={false} field={field}
                  inputPlaceholder="Suite" inputId="Suite" error={error}
                />
              )}
            />
          </div>
        </div>

        <div className="row">
          <label className="form-label col-lg-2 col-md-2 col-12">City State, Zip *</label>
          <div className="col-lg-4 col-md-6">
            <Controller
              name="patient_attributes.address.city"
              control={control}
              defaultValue=""
              render={({ field, fieldState: { error } }) => (
                <HubInputField
                  isRowBased={true}
                  isRowInsideAnother={true}
                  isRequired={true}
                  field={field}
                  inputPlaceholder="City *"
                  inputId="patient_attributes.address.city"
                  error={error}
                />
              )}
            />
          </div>

          <div className="col-lg-3 col-md-6">
            <Controller
              name="patient_attributes.address.state"
              control={control}
              defaultValue=""
              render={({ field, fieldState: { error } }) => (
                <HubInputField
                  inputId="patient_attributes.address.state"
                  isRequired={true}
                  inputPlaceholder="Select a State* ..."
                  isRowBased={true}
                  field={field}
                  error={error}
                  isDropdown={true}
                  isSearchable={false}
                  isTypeAhead
                  dropdownData={
                    US_STATES.map(
                      (item) => ({ value: item.value, label: item.value })
                    ) as unknown as OptionsOrGroups<string, GroupBase<string>>
                  }
                />
              )}
            />
          </div>

          <div className="col-lg-3 col-md-6">
            <Controller
              name="patient_attributes.address.zip"
              control={control}
              defaultValue=""
              render={({ field, fieldState: { error } }) => (
                <HubInputField
                  inputId="patient_attributes.address.zip"
                  inputPlaceholder="Zip *"
                  isRowBased={true}
                  isRequired={false}
                  field={field}
                  error={error}
                />
              )}
            />
          </div>
        </div>

        <Controller
          name="patient_attributes.address.address_name"
          control={control}
          defaultValue="home"
          render={({ field, fieldState: { error } }) => (
            <HubInputField
              inputId="patient_attributes.address.address_name"
              isRequired={true}
              isRowBased={true}
              inputLabel="Type"
              field={field}
              error={error}
              isDropdown={true}
              isSearchable={false}
              isTypeAhead
              inputPlaceholder="Select address type ..."
              dropdownData={PATIENT_ADDR_TYPES as unknown as OptionsOrGroups<string, GroupBase<string>>}
            />
          )}
        />
      </div>
    </div>
  );
};

export default AddCasePatientAddress;
